import { Loader2, Trash2, TrashIcon } from 'lucide-react';
import React, { ComponentPropsWithoutRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { RefreshToken } from '@crossnokaye/typescript-sdk/models/components';
import { Button, useToaster, withTooltip } from '@crossnokaye/ux-common';
import { ConditionalRender } from '@crossnokaye/ux-smarts';

import { useRevokeRefreshToken } from '@/features/tokens/hooks/useRevokeRefreshToken';
const ButtonWithTooltip = withTooltip(Button);

interface RevokeTokenActionProps extends ComponentPropsWithoutRef<typeof Button> {
  token: RefreshToken;
  onActionSuccess?: () => void;
}

export const RevokeTokenAction: React.FC<RevokeTokenActionProps> = ({
  token,
  variant = 'ghost',
  size = 'icon',
  onActionSuccess,
  ...props
}) => {
  const queryClient = useQueryClient();
  const { addSuccessToast, addErrorToast } = useToaster();

  const { mutate: revokeToken, isPending } = useRevokeRefreshToken({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['iam', 'ListUserRefreshTokens'] });
      addSuccessToast('Token revoked successfully');
      onActionSuccess?.();
    },
    onError: () => {
      addErrorToast('Failed to revoke token');
    },
  });

  const handleRevoke = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    revokeToken({ tokenId: token.id, userId: token.principal.principalId });
  };

  return (
    <ButtonWithTooltip
      variant={variant}
      size={size}
      onClick={handleRevoke}
      disabled={isPending}
      tooltipContent={
        <span className={'flex flex-row gap-2 items-center'}>
          <TrashIcon className={'h-4 w-4'} />
          {'Revoke Token'}
        </span>
      }
      {...props}
    >
      <ConditionalRender condition={isPending} fallback={<Trash2 className={'h-4 w-4'} />}>
        <Loader2 className={'h-4 w-4 animate-spin'} />
      </ConditionalRender>
      <ConditionalRender condition={size !== 'icon'}>
        <span className={'ml-2'}>{'Revoke'}</span>
      </ConditionalRender>
    </ButtonWithTooltip>
  );
};
